import React, { useEffect, useState } from "react";
import { GetRepresentativeDetails } from "../../../../services/collegeRequests";

const RepresentativeDetails = ({
  data,
  setTab,
  setType,
  setRefresh,
  refresh,
}) => {
  const [representativeData, setRepresentativeData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRepresentativeData = async () => {
      try {
        setLoading(true);
        // Use the request_id from the data passed from parent component
        const response = await GetRepresentativeDetails(data.request_id);

        if (response && response.data.statusCode === 200) {
          setRepresentativeData(response.data.result);
        } else {
          console.error("Failed to fetch representative details");
        }
      } catch (error) {
        console.error("Error fetching representative details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (data && data.request_id) {
      fetchRepresentativeData();
    }
  }, [data]);

  // Get full name
  const getFullName = () => {
    const firstName = representativeData?.first_name || "";
    const middleName = representativeData?.middle_name || "";
    const lastName = representativeData?.last_name || "";
    return `${firstName} ${middleName} ${lastName}`.trim() || "NA";
  };

  // Format mobile number with country code
  const formatMobile = (countryCode, mobile) => {
    if (countryCode && mobile) {
      return `${countryCode} ${mobile}`;
    }
    return mobile || "NA";
  };

  if (loading) {
    return (
      <div className="rep-dtl-container">
        <div className="rep-dtl-loading">
          <div className="rep-dtl-spinner"></div>
          <p>Loading representative data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rep-dtl-container">
      {/* Representative Header */}
      <div className="rep-dtl-header">
        <h1 className="rep-dtl-main-title">Representative Details</h1>
      </div>

      {/* Representative Info Card */}
      <div className="rep-dtl-info-card">
        <div className="rep-dtl-info-header">
          <div className="rep-dtl-avatar">
            <div className="rep-dtl-avatar-placeholder">
              <span>{representativeData?.first_name?.charAt(0) || "R"}</span>
            </div>
          </div>
          <div className="rep-dtl-title-section">
            <h2 className="rep-dtl-representative-name">{getFullName()}</h2>
            <div className="rep-dtl-designation-badge">
              <span className="rep-dtl-position">
                {representativeData?.position || "Representative"}
              </span>
              {representativeData?.department && (
                <span className="rep-dtl-department">
                  • {representativeData.department}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="rep-dtl-section">
        <h3 className="rep-dtl-section-title">Personal Information</h3>
        <div className="rep-dtl-section-content">
          <div className="rep-dtl-details-grid">
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Full Name</span>
              <span className="rep-dtl-detail-value">{getFullName()}</span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Designation</span>
              <span className="rep-dtl-detail-value">
                {representativeData?.position || "NA"}
              </span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Department</span>
              <span className="rep-dtl-detail-value">
                {representativeData?.department || "NA"}
              </span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Referral Code</span>
              <span className="rep-dtl-detail-value rep-dtl-referral-code">
                {representativeData?.referral_code || "NA"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information Section */}
      <div className="rep-dtl-section">
        <h3 className="rep-dtl-section-title">Contact Information</h3>
        <div className="rep-dtl-section-content">
          <div className="rep-dtl-details-grid">
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Official Email</span>
              <span className="rep-dtl-detail-value rep-dtl-email">
                {representativeData?.official_email || "NA"}
              </span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Personal Email</span>
              <span className="rep-dtl-detail-value rep-dtl-email">
                {representativeData?.personal_email || "NA"}
              </span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Primary Mobile</span>
              <span className="rep-dtl-detail-value">
                {formatMobile(
                  representativeData?.mob_country_code,
                  representativeData?.mobile_no
                )}
              </span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Alternative Mobile</span>
              <span className="rep-dtl-detail-value">
                {formatMobile(
                  representativeData?.alternative_mob_country_code,
                  representativeData?.alternative_mobile
                )}
              </span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">LinkedIn Profile</span>
              <span className="rep-dtl-detail-value rep-dtl-linkedin">
                {representativeData?.linkedin_link ? (
                  <a
                    href={representativeData.linkedin_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Profile
                  </a>
                ) : (
                  "NA"
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Address Information Section */}
      <div className="rep-dtl-section">
        <h3 className="rep-dtl-section-title">Address Information</h3>
        <div className="rep-dtl-section-content">
          <div className="rep-dtl-details-grid">
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">Address</span>
              <span className="rep-dtl-detail-value">
                {representativeData?.address || "NA"}
              </span>
            </div>
            <div className="rep-dtl-detail-item">
              <span className="rep-dtl-detail-label">City</span>
              <span className="rep-dtl-detail-value">
                {representativeData?.city || "NA"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepresentativeDetails;
